// app/providers.tsx
"use client";

import { createContext, useContext } from "react";

import type { default as React } from "react";
import { type PostHogProps, PostHogProvider, usePostHog } from "./post-hog-provider";

type ProductTrackingContextType = {
	capture: (
		event: {
			feature: string;
			action: string;
			object: string;
			context?: string;
		},
		properties?: Record<string, any>,
	) => void;
	group: (properties: {
		companyId: number;
		customerType?: "Operator" | "Higher_Education";
	}) => void;
	identify: (properties: {
		key?: string;
		email?: string;
		companyId?: number;
	}) => void;
};

const ProductTrackingContext = createContext<ProductTrackingContextType>({
	capture: () => {
		throw new Error("useProductAnalytics must be used within a ProductAnalyticsProvider");
	},
	group: () => {
		throw new Error("useProductAnalytics must be used within a ProductAnalyticsProvider");
	},
	identify: () => {
		throw new Error("useProductAnalytics must be used within a ProductAnalyticsProvider");
	},
});

export const useProductTracking = () => {
	return useContext(ProductTrackingContext);
};

export const ProductTrackingProvider = ({ children, ...props }: React.PropsWithChildren<PostHogProps>) => (
	<PostHogProvider {...props}>
		<ProductTrackingContextWrapper>{children}</ProductTrackingContextWrapper>
	</PostHogProvider>
);

const useProvideProductTracking = () => {
	const posthog = usePostHog();

	const identify: ProductTrackingContextType["identify"] = ({ key: userId, ...properties }) => {
		if (userId) {
			posthog?.identify(userId, properties);
		}
	};

	const group: ProductTrackingContextType["group"] = ({ companyId, customerType }) => {
		posthog?.group("company", companyId?.toString(), {
			customerType,
		});
	};

	const capture: ProductTrackingContextType["capture"] = (event, properties) => {
		const { feature = "unknown", action = "unknown", object = "unknown", context } = event;
		posthog?.capture([feature, action, object, context].filter(Boolean).join("."), { ...event, ...properties });
	};

	return { identify, group, capture };
};

const ProductTrackingContextWrapper = ({ children }: { children: React.ReactNode }) => {
	const value = useProvideProductTracking();
	return <ProductTrackingContext.Provider value={value}>{children}</ProductTrackingContext.Provider>;
};
